import { useEffect, useMemo, useRef, useState } from 'react';
import { EventType } from 'src/gql/graphql';
import { useActiveSheetPagination } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheetPagination';
import { useLoadPDF } from 'src/components/viewers/SheetsViewerV2/hooks/useLoadPDF';
import { AggregatedView } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetsAggregatedView';
import { MappedPDFData } from 'src/components/viewers/SheetsViewerV2/mappers/mapPDFData';
import { createSheetPageDiffPdf } from 'src/components/viewers/SheetsViewerV2/utils/sheetDiffPdf';
import {
  getSheetUrl,
  getSheetUrlByS3Key,
} from 'src/components/viewers/SheetsViewerV2/utils/s3';
import { useProjectHistory } from 'src/components/project/project-history/hooks/useProjectHistory';
import {
  getHistoricalSheets,
  HistoricalSheetVersionPair,
} from 'src/components/project/project-history/hooks/useDiffing';
import { useLocalStorage } from 'src/hooks/local-storage';
import { useS3Client } from 'src/hooks/s3';
import { Sheet, useSheets } from 'src/hooks/sheets';
import { useUserTenant } from 'src/services/auth-info';

type Props = { projectId: string; aggregatedView: AggregatedView | null };

export type ActiveSheetData = {
  id: string;
  page: number;
  projectId: string;
} & MappedPDFData;
export type ActiveSheet = {
  isLoading: boolean;
  data?: ActiveSheetData;
  error?: Error;
};
const InitActiveSheetState = { isLoading: true };

type HistoricalSheetsState = {
  sheets: Sheet[];
  eventTypeByName: Record<string, EventType | undefined>;
  versionPairByName: Record<string, HistoricalSheetVersionPair>;
};

type DiffPdfCacheEntry = {
  objectUrl: string;
  viewerUrl: string;
  totalPages: number;
};

const buildHistoricalSheetS3Key = (
  tenantGroup: string,
  projectId: string,
  sheet: Pick<Sheet, 'version' | 'filename'>
) => `${tenantGroup}/project/${projectId}/sheets/${sheet.version}/${sheet.filename}`;

const enrichHistoricalSheet = (
  sheet: Sheet | undefined,
  tenantGroup: string,
  projectId: string
): Sheet | undefined => {
  if (!sheet || typeof sheet.version !== 'number' || !sheet.filename) {
    return undefined;
  }
  return {
    ...sheet,
    s3Key: buildHistoricalSheetS3Key(tenantGroup, projectId, sheet),
  };
};

export const useActiveSheet = ({ projectId, aggregatedView }: Props) => {
  const debounceRef = useRef<number>();
  const requestIdRef = useRef(0);
  const diffPdfCacheRef = useRef<Map<string, DiffPdfCacheEntry>>(new Map());
  const s3Client = useS3Client(projectId);
  const { sheets } = useSheets(projectId);
  const { tenant } = useUserTenant();
  const { isHistoryDrawerOpen, referenceState, diff } = useProjectHistory();
  const { loadPDF, unloadPDF } = useLoadPDF(aggregatedView);
  const [activeSheetId, changeActiveSheet] = useLocalStorage<string | null>(
    `${projectId}-active-sheet-id`,
    null
  );
  const [activeSheet, setActiveSheet] =
    useState<ActiveSheet>(InitActiveSheetState);
  const pagination = useActiveSheetPagination(activeSheet.data);

  const historicalSheetsState: HistoricalSheetsState | null = useMemo(() => {
    const tenantGroup = tenant?.group;
    if (!isHistoryDrawerOpen || !referenceState || !diff || !tenantGroup) {
      return null;
    }

    const historical = getHistoricalSheets(referenceState, diff, projectId);
    const historicalSheets = historical.sheets
      .map((sheet) => enrichHistoricalSheet(sheet, tenantGroup, projectId))
      .filter((sheet): sheet is Sheet => Boolean(sheet));
    const versionPairByName: Record<string, HistoricalSheetVersionPair> = {};
    Object.entries(historical.versionPairByName).forEach(([name, pair]) => {
      versionPairByName[name] = {
        old: enrichHistoricalSheet(pair.old, tenantGroup, projectId),
        new: enrichHistoricalSheet(pair.new, tenantGroup, projectId),
      };
    });

    return {
      sheets: historicalSheets,
      eventTypeByName: historical.eventTypeByName,
      versionPairByName,
    };
  }, [diff, isHistoryDrawerOpen, projectId, referenceState, tenant?.group]);

  const effectiveSheets: Sheet[] = useMemo(() => {
    if (historicalSheetsState) {
      return historicalSheetsState.sheets;
    }
    return sheets || [];
  }, [historicalSheetsState, sheets]);

  const modifiedSheetDiffPair = useMemo(() => {
    if (!isHistoryDrawerOpen || !historicalSheetsState || !activeSheetId) {
      return null;
    }

    const activeSheetModel = effectiveSheets.find(
      (sheet) => sheet.id === activeSheetId
    );
    const activeName = activeSheetModel?.name;
    if (!activeName) return null;
    if (historicalSheetsState.eventTypeByName[activeName] !== EventType.Modified) {
      return null;
    }
    const pair = historicalSheetsState.versionPairByName[activeName];
    if (!pair?.old?.s3Key || !pair?.new?.s3Key) return null;

    return {
      oldSheet: pair.old,
      newSheet: pair.new,
    };
  }, [activeSheetId, effectiveSheets, historicalSheetsState, isHistoryDrawerOpen]);

  useEffect(() => {
    return () => {
      diffPdfCacheRef.current.forEach((entry) =>
        URL.revokeObjectURL(entry.objectUrl)
      );
      diffPdfCacheRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (isHistoryDrawerOpen) return;

    diffPdfCacheRef.current.forEach((entry) =>
      URL.revokeObjectURL(entry.objectUrl)
    );
    diffPdfCacheRef.current.clear();
  }, [isHistoryDrawerOpen]);

  // In history mode, if the current active sheet doesn't exist, pick by same name or first
  useEffect(() => {
    if (!isHistoryDrawerOpen) return;
    if (!effectiveSheets || effectiveSheets.length === 0) return;

    const existsInHistorical = Boolean(
      activeSheetId && effectiveSheets.some((s) => s.id === activeSheetId)
    );
    if (existsInHistorical) return;

    const resolveFallbackId = () => {
      const currentName = activeSheetId
        ? [...(sheets || []), ...effectiveSheets].find(
            (s) => s.id === activeSheetId
          )?.name
        : undefined;
      if (currentName) {
        const sameName = effectiveSheets
          .filter((s) => s.name === currentName)
          .sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
        if (sameName.length > 0) return sameName[0].id;
      }
      return effectiveSheets[0]?.id ?? null;
    };

    const fallbackId = resolveFallbackId();
    if (fallbackId) changeActiveSheet(fallbackId);
  }, [
    isHistoryDrawerOpen,
    effectiveSheets,
    activeSheetId,
    sheets,
    changeActiveSheet,
  ]);

  // Handle the case when there are no sheets or no activeSheetId
  useEffect(() => {
    // This effect could have probably been combined with the one below it
    // but no time to test edge cases now so will leave it as is
    if (!effectiveSheets || effectiveSheets.length === 0) {
      requestIdRef.current += 1;
      // unload the pdf model from the viewer
      unloadPDF();
      setActiveSheet({ isLoading: false });
    }
    // Added the activeSheetId so that if we have a rapid opening and closing of the history drawer,
    // we can be able to catch loaded PDFs since after a successful load, we set the active sheet
  }, [effectiveSheets, activeSheetId, unloadPDF]);

  useEffect(() => {
    if (!effectiveSheets || !activeSheetId) return;
    // reset pagination after changing a sheet model
    if (activeSheetId !== activeSheet?.data?.id) pagination.setPageNumber(1);
    // used debounce effect to prevent doubled loading of the sheet
    if (debounceRef.current) window.clearTimeout(debounceRef.current);

    const requestId = ++requestIdRef.current;
    debounceRef.current = window.setTimeout(() => {
      void (async () => {
        setActiveSheet((prevState) => ({ ...prevState, isLoading: true }));
        const requestedPage = pagination.pageNumber;

        try {
          let sheetUrl = await getSheetUrl(s3Client, effectiveSheets, activeSheetId);
          let viewerPage = requestedPage;
          let totalPagesOverride: number | undefined;

          if (!sheetUrl) {
            throw new Error('Could not load pdf');
          }

          if (modifiedSheetDiffPair) {
            const diffCacheKey = `${modifiedSheetDiffPair.oldSheet.id}:${modifiedSheetDiffPair.newSheet.id}:${requestedPage}`;
            const cachedDiff = diffPdfCacheRef.current.get(diffCacheKey);

            if (cachedDiff) {
              sheetUrl = cachedDiff.viewerUrl;
              viewerPage = 1;
              totalPagesOverride = cachedDiff.totalPages;
            } else {
              try {
                const [oldUrl, newUrl] = await Promise.all([
                  getSheetUrlByS3Key(s3Client, modifiedSheetDiffPair.oldSheet.s3Key),
                  getSheetUrlByS3Key(s3Client, modifiedSheetDiffPair.newSheet.s3Key),
                ]);
                const { pdfBlob, totalPages } = await createSheetPageDiffPdf({
                  oldPdfUrl: oldUrl,
                  newPdfUrl: newUrl,
                  pageNumber: requestedPage,
                });
                const fileBackedPdf = new File(
                  [pdfBlob],
                  `history-sheet-diff-page-${requestedPage}.pdf`,
                  {
                    type: 'application/pdf',
                  }
                );
                const diffObjectUrl = URL.createObjectURL(fileBackedPdf);
                // Autodesk loader can reject bare blob URLs without extension.
                const diffViewerUrl = `${diffObjectUrl}#history-sheet-diff.pdf`;
                diffPdfCacheRef.current.set(diffCacheKey, {
                  objectUrl: diffObjectUrl,
                  viewerUrl: diffViewerUrl,
                  totalPages,
                });
                sheetUrl = diffViewerUrl;
                viewerPage = 1;
                totalPagesOverride = totalPages;
              } catch (diffError) {
                console.error(
                  'Failed to generate sheet diff overlay, falling back to latest sheet',
                  diffError
                );
              }
            }
          }

          const data = await loadPDF(projectId, activeSheetId, sheetUrl, viewerPage);
          if (requestId !== requestIdRef.current) return;

          setActiveSheet({
            data: {
              ...data,
              page: requestedPage,
              totalPages: totalPagesOverride ?? data.totalPages,
            },
            isLoading: false,
          });
        } catch (error) {
          if (requestId !== requestIdRef.current) return;
          setActiveSheet({
            error:
              error instanceof Error
                ? error
                : new Error('Failed to load sheet PDF'),
            isLoading: false,
          });
        }
      })();
    }, 50);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [
    activeSheetId,
    activeSheet?.data?.id,
    effectiveSheets,
    modifiedSheetDiffPair,
    pagination.pageNumber,
    pagination.setPageNumber,
    projectId,
    s3Client,
    loadPDF,
  ]);

  return {
    sheets: effectiveSheets,
    activeSheet,
    activeSheetId,
    pagination,
    setPageNumber: pagination.setPageNumber,
    changeActiveSheet,
  };
};
